import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Badge,
  Button,
  Dropdown,
  Option,
  Input,
  Spinner,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  tokens,
} from '@fluentui/react-components';
import {
  PersonRegular,
  CalendarRegular,
  CheckmarkCircleRegular,
  ClockRegular,
  PeopleRegular,
  AddRegular,
  BuildingRegular,
  OpenRegular,
  DismissCircleRegular,
  LinkRegular,
  SearchRegular,
  EditRegular,
  CheckmarkRegular,
  MailRegular,
  PhoneRegular,
  DeleteRegular,
  PeopleCommunityRegular,
  ArrowSwapRegular,
} from '@fluentui/react-icons';
import { format } from 'date-fns';
import { getDataverseRecordUrl, isDataverseConfigured } from '../../utils/dataverse';
import {
  searchDataverse,
  getOutreachHistory,
  getTaxReturnIrsNotices,
} from '../../services/api';
import { dueBucket, daysUntilDue, formatDueLabel } from '../../utils/irsNoticeDue';
import {
  statusDisplayNames,
  statusColors,
  appointmentTypeDisplayNames,
  appointmentTypeColors,
  requiresRemoteSignature,
  getScheduleCBusinesses,
  type TaxReturn,
  type ScheduleCBusiness,
  type ClientBookkeepingStatus,
  type ReviewStatus,
  type ContactInfo,
  type AccountInfo,
  type SearchResults,
  type AppointmentType,
  type IrsNotice,
  irsNoticeStatusDisplayNames,
  irsNoticeStatusColors,
} from '../../types';
import { REVIEW_STATUS_DISPLAY_NAMES, REVIEW_STATUS_COLORS } from '../../constants';
import { canRecordSpouse } from './spouseAffordance';
import { buildContactRelinkPayload, buildAccountRelinkPayload } from './relinkPayload';
import { buildRelinkConsequence } from './relinkConsequence';

const reviewStatusOptions: ReviewStatus[] = [
  'not_started',
  'in_progress',
  'completed',
  'completed_pending_payment',
  'changes_required_preparer',
  'changes_required_taxpayer',
];

const efileStatusColors: Record<string, string> = {
  pending: 'var(--tl-text-tertiary)',
  submitted: 'var(--tl-brand)',
  accepted: 'var(--tl-success)',
  rejected: 'var(--tl-danger)',
};

export interface OverviewTabProps {
  taxReturn: TaxReturn;
  linkedReturns?: TaxReturn[];
  appointmentReturns?: TaxReturn[];
  bookkeepingStatus?: ClientBookkeepingStatus;
  onAddReturnDialogOpen: () => void;
  onMarkInactiveOpen: () => void;
  onAddSpouseOpen: () => void;
  onMergeSpouseOpen: () => void;
  onMergeScheduleCOpen: () => void;
  onAddBusinessOpen: () => void;
  onSwapTaxpayerSpouse: () => void;
  swapTaxpayerSpousePending?: boolean;
  onReviewStatusChange: (status: ReviewStatus) => void;
  reviewStatusUpdating?: boolean;
  onUpdateTaxReturn?: (updates: Partial<TaxReturn>) => void;
}

export function OverviewTab({
  taxReturn,
  appointmentReturns,
  bookkeepingStatus,
  onAddReturnDialogOpen,
  onMarkInactiveOpen,
  onAddSpouseOpen,
  onMergeSpouseOpen,
  onMergeScheduleCOpen,
  onAddBusinessOpen,
  onSwapTaxpayerSpouse,
  swapTaxpayerSpousePending,
  onReviewStatusChange,
  reviewStatusUpdating,
  onUpdateTaxReturn,
}: OverviewTabProps) {
  const { t } = useTranslation();

  // E-File status badge label. Rebuilt with useMemo (not a module-scope Record) because it needs
  // t(), following the filingTypeLabels pattern from TaxReturnDetailPage.
  const efileStatusLabels: Record<TaxReturn['efileStatus'], string> = useMemo(() => ({
    pending: t('overviewTab.efileStatusLabels.pending'),
    submitted: t('overviewTab.efileStatusLabels.submitted'),
    accepted: t('overviewTab.efileStatusLabels.accepted'),
    rejected: t('overviewTab.efileStatusLabels.rejected'),
  }), [t]);

  // Signature status badge label — same shape as efileStatusLabels above. "Sign On-Site" is a
  // separate override for in-person appointments, handled where the badge is rendered.
  const signatureStatusLabels: Record<TaxReturn['signatureStatus'], string> = useMemo(() => ({
    pending: t('overviewTab.signatureStatusLabels.pending'),
    sent: t('overviewTab.signatureStatusLabels.sent'),
    signed: t('overviewTab.signatureStatusLabels.signed'),
  }), [t]);

  // Dataverse link state (primary contact)
  const [showLinkSearch, setShowLinkSearch] = useState(false);
  // S2 Q3 — the chosen relink, held until the consequence has been shown and accepted.
  const [pendingRelink, setPendingRelink] = useState<Partial<TaxReturn> | null>(null);
  const [linkSearchQuery, setLinkSearchQuery] = useState('');
  const [linkSearchResults, setLinkSearchResults] = useState<SearchResults | null>(null);
  const [isLinkSearching, setIsLinkSearching] = useState(false);

  // Dataverse link state (spouse)
  const [showSpouseLinkSearch, setShowSpouseLinkSearch] = useState(false);
  const [spouseLinkSearchQuery, setSpouseLinkSearchQuery] = useState('');
  const [spouseLinkSearchResults, setSpouseLinkSearchResults] = useState<SearchResults | null>(null);
  const [isSpouseLinkSearching, setIsSpouseLinkSearching] = useState(false);

  // Remove spouse confirmation state
  const [confirmRemoveSpouse, setConfirmRemoveSpouse] = useState(false);

  // Swap taxpayer/spouse confirmation state
  const [confirmSwapTaxpayer, setConfirmSwapTaxpayer] = useState(false);

  // Inline editing state for client
  const [editingClientName, setEditingClientName] = useState(false);
  const [editingClientEmail, setEditingClientEmail] = useState(false);
  const [editingClientPhone, setEditingClientPhone] = useState(false);
  const [clientNameDraft, setClientNameDraft] = useState('');
  const [clientEmailDraft, setClientEmailDraft] = useState('');
  const [clientPhoneDraft, setClientPhoneDraft] = useState('');

  // Inline editing state for spouse
  const [editingSpouseName, setEditingSpouseName] = useState(false);
  const [editingSpouseEmail, setEditingSpouseEmail] = useState(false);
  const [editingSpousePhone, setEditingSpousePhone] = useState(false);
  const [spouseNameDraft, setSpouseNameDraft] = useState('');
  const [spouseEmailDraft, setSpouseEmailDraft] = useState('');
  const [spousePhoneDraft, setSpousePhoneDraft] = useState('');

  // Schedule C Business state
  const [editingBusinessName, setEditingBusinessName] = useState(false);
  const [editingBusinessEIN, setEditingBusinessEIN] = useState(false);
  const [editingBusinessPhone, setEditingBusinessPhone] = useState(false);
  const [businessNameDraft, setBusinessNameDraft] = useState('');
  const [businessEINDraft, setBusinessEINDraft] = useState('');
  const [businessPhoneDraft, setBusinessPhoneDraft] = useState('');
  const [showBusinessLinkSearch, setShowBusinessLinkSearch] = useState(false);
  const [businessLinkSearchQuery, setBusinessLinkSearchQuery] = useState('');
  const [businessLinkSearchResults, setBusinessLinkSearchResults] = useState<AccountInfo[] | null>(null);
  const [isBusinessLinkSearching, setIsBusinessLinkSearching] = useState(false);
  const [confirmRemoveBusiness, setConfirmRemoveBusiness] = useState(false);

  const performLinkSearch = useCallback(async (query: string) => {
    if (query.trim().length < 2) {
      setLinkSearchResults(null);
      return;
    }
    setIsLinkSearching(true);
    try {
      const results = await searchDataverse(query.trim());
      setLinkSearchResults(results);
    } catch {
      setLinkSearchResults(null);
    } finally {
      setIsLinkSearching(false);
    }
  }, []);

  const performSpouseLinkSearch = useCallback(async (query: string) => {
    if (query.trim().length < 2) {
      setSpouseLinkSearchResults(null);
      return;
    }
    setIsSpouseLinkSearching(true);
    try {
      const results = await searchDataverse(query.trim());
      setSpouseLinkSearchResults(results);
    } catch {
      setSpouseLinkSearchResults(null);
    } finally {
      setIsSpouseLinkSearching(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (linkSearchQuery.trim().length >= 2) {
        performLinkSearch(linkSearchQuery);
      } else {
        setLinkSearchResults(null);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [linkSearchQuery, performLinkSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (spouseLinkSearchQuery.trim().length >= 2) {
        performSpouseLinkSearch(spouseLinkSearchQuery);
      } else {
        setSpouseLinkSearchResults(null);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [spouseLinkSearchQuery, performSpouseLinkSearch]);

  // S2 — RELINK, NOT RENAME.
  //
  // Linking used to set only contactId/contactName, which left the return DISPLAYING the
  // previous person's name next to a freshly-linked contact. The only control on screen that
  // could fix that display was the client-name box — and before PR #71 editing it PATCHed the
  // shared Dataverse contact. That is how a relink turned into a rename.
  //
  // The spouse handler below has always set all of its fields together, which is why nobody
  // ever hand-edited spouse names. This makes the client path match it.
  //
  // ⚠ clientId is deliberately NOT changed. It is not a display field: ~123 call sites key
  // outreach history, invoices, inquiries and message threads off it, and the update endpoint
  // rejects it by design (see TaxReturnFunctions.ApplyUpdates). Re-pointing a return's whole
  // client identity is a separate, specced change — not a side-effect of picking a contact.
  // S2 Q3 — the counts behind the confirmation, at their two different grains.
  //
  // The invoice is RETURN-scoped (this return has one or it does not). Outreach is
  // CLIENT-scoped and spans the old client's other returns. The sentence states both scopes
  // rather than adding them together: a staff member reading a single merged total on one
  // return would assume it was all that return's and be wrong.
  //
  // Fetched imperatively when a relink is pending, so browsing the tab costs nothing.
  //
  // ⚠ Deliberately NOT useQuery. This tab is rendered without a QueryClientProvider by
  // OverviewTab.dataverseSync.test.tsx, and a useQuery here made the whole component throw
  // "No QueryClient set" in that test. The fix is to not impose the requirement rather than to
  // edit a passing test into supplying it: a presentational tab should not acquire a
  // react-query dependency because one dialog inside it needs one number.
  const [outreachCount, setOutreachCount] = useState<number | null>(null);

  useEffect(() => {
    if (!pendingRelink || !taxReturn.clientId) return;
    let cancelled = false;
    setOutreachCount(null);
    getOutreachHistory(taxReturn.taxYear, taxReturn.clientId)
      .then((rows) => { if (!cancelled) setOutreachCount(rows.length); })
      // A failed count must not render as a measured zero — leave it unloaded so the sentence
      // says it is still checking rather than claiming there is nothing to leave behind.
      .catch(() => { if (!cancelled) setOutreachCount(null); });
    return () => { cancelled = true; };
  }, [pendingRelink, taxReturn.clientId, taxReturn.taxYear]);

  // IRS letters / notices recorded against THIS return.
  //
  // ⚠ Deliberately NOT useQuery, for the reason stated above the outreach count: this tab is
  // rendered without a QueryClientProvider by OverviewTab.dataverseSync.test.tsx, and a useQuery
  // here makes the whole component throw "No QueryClient set". A presentational tab should not
  // acquire a react-query dependency because one card inside it needs a list.
  //
  // Three states, not two. `null` means NOT LOADED and `[]` means MEASURED ZERO — a failed fetch
  // must never render as "this return has no notices", which is a claim nobody verified. And the
  // failure is SHOWN rather than swallowed: a feature that silently displays nothing when it is
  // broken is indistinguishable from one with nothing to display, which is how a dead panel
  // survives for months.
  const [irsNotices, setIrsNotices] = useState<IrsNotice[] | null>(null);
  const [irsNoticesFailed, setIrsNoticesFailed] = useState(false);

  useEffect(() => {
    if (!taxReturn.id) return;
    let cancelled = false;
    setIrsNotices(null);
    setIrsNoticesFailed(false);
    // clientId is passed when known so the server scopes the Cosmos query to one partition
    // instead of fanning out.
    getTaxReturnIrsNotices(taxReturn.id, taxReturn.clientId || undefined)
      .then((rows) => {
        if (!cancelled) setIrsNotices(rows);
      })
      .catch(() => {
        if (!cancelled) {
          setIrsNotices(null);
          setIrsNoticesFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [taxReturn.id, taxReturn.clientId]);

  const openIrsNotices = (irsNotices ?? []).filter((n) => !n.resolvedAt);

  const relinkConsequence = pendingRelink
    ? buildRelinkConsequence({
        hasInvoice: Boolean(taxReturn.invoiceId || taxReturn.clientInvoice?.invoiceNumber),
        outreachCount: outreachCount ?? 0,
        oldClientName: taxReturn.clientName ?? '',
        outreachLoaded: outreachCount !== null,
      })
    : null;

  // S2 Q3 — confirm before applying, with COUNTS at their stated grains.
  //
  // The relink is allowed at any return status: refusing above a threshold would refuse the
  // exact case relink exists for (a business changing hands mid-season is precisely a return
  // that is already filed) and there is no support path to refuse it into. So the control is a
  // confirmation that names what stays behind, not a prohibition.
  const applyRelink = (payload: Partial<TaxReturn>) => {
    onUpdateTaxReturn?.(payload);
    setPendingRelink(null);
    setShowLinkSearch(false);
    setLinkSearchQuery('');
    setLinkSearchResults(null);
  };

  const handleLinkContact = (contact: ContactInfo) => {
    setPendingRelink(buildContactRelinkPayload(contact, taxReturn) as Partial<TaxReturn>);
  };

  const handleLinkAccount = (account: AccountInfo) => {
    setPendingRelink(buildAccountRelinkPayload(account, taxReturn) as Partial<TaxReturn>);
  };

  const handleUnlinkContact = () => {
    onUpdateTaxReturn?.({ contactId: null, contactName: null });
  };

  const handleLinkSpouseContact = (contact: ContactInfo) => {
    onUpdateTaxReturn?.({
      spouseId: contact.contactId,
      spouseName: contact.fullName,
      spouseEmail: contact.email || taxReturn.spouseEmail,
      spousePhone: contact.phone || contact.mobilePhone || taxReturn.spousePhone,
    } as Partial<TaxReturn>);
    setShowSpouseLinkSearch(false);
    setSpouseLinkSearchQuery('');
    setSpouseLinkSearchResults(null);
  };

  const handleUnlinkSpouseContact = () => {
    onUpdateTaxReturn?.({ spouseId: null });
  };

  // Client/spouse name, email and phone are LOCAL LABELS on this return. Editing them
  // does not touch the Dataverse contact — the contact is a SHARED record referenced by
  // other returns and appointments, so a per-return edit cannot know it was meant to
  // apply there. (Removed in PR #71; the write-back no longer exists.)
  // To change WHICH client this return points at, use relink — never rename-in-place.
  const handleSaveClientName = () => {
    const trimmed = clientNameDraft.trim();
    if (!trimmed) return;
    onUpdateTaxReturn?.({ clientName: trimmed } as Partial<TaxReturn>);
    setEditingClientName(false);
  };

  const handleSaveClientEmail = () => {
    onUpdateTaxReturn?.({ clientEmail: clientEmailDraft || undefined } as Partial<TaxReturn>);
    setEditingClientEmail(false);
  };

  const handleSaveClientPhone = () => {
    onUpdateTaxReturn?.({ clientPhone: clientPhoneDraft || undefined } as Partial<TaxReturn>);
    setEditingClientPhone(false);
  };

  const handleSaveSpouseName = () => {
    const trimmed = spouseNameDraft.trim();
    if (!trimmed) return;
    onUpdateTaxReturn?.({ spouseName: trimmed } as Partial<TaxReturn>);
    setEditingSpouseName(false);
  };

  const handleSaveSpouseEmail = () => {
    onUpdateTaxReturn?.({ spouseEmail: spouseEmailDraft || undefined } as Partial<TaxReturn>);
    setEditingSpouseEmail(false);
  };

  const handleSaveSpousePhone = () => {
    onUpdateTaxReturn?.({ spousePhone: spousePhoneDraft || undefined } as Partial<TaxReturn>);
    setEditingSpousePhone(false);
  };

  const handleRemoveSpouse = () => {
    onUpdateTaxReturn?.({
      spouseId: null,
      spouseName: null,
      spouseEmail: null,
      spousePhone: null,
      filingType: 'single',
    });
    setConfirmRemoveSpouse(false);
  };

  // Schedule C Business handlers
  const performBusinessLinkSearch = useCallback(async (query: string) => {
    if (query.trim().length < 2) {
      setBusinessLinkSearchResults(null);
      return;
    }
    setIsBusinessLinkSearching(true);
    try {
      const results = await searchDataverse(query.trim(), 'account');
      setBusinessLinkSearchResults(results.accounts || []);
    } catch {
      setBusinessLinkSearchResults(null);
    } finally {
      setIsBusinessLinkSearching(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (businessLinkSearchQuery.trim().length >= 2) {
        performBusinessLinkSearch(businessLinkSearchQuery);
      } else {
        setBusinessLinkSearchResults(null);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [businessLinkSearchQuery, performBusinessLinkSearch]);

  // Schedule C businesses are stored as a canonical list; the legacy scalar fields mirror the primary
  // (first) business. All edits below write the list so it stays the source of truth; the backend
  // re-mirrors the primary into the scalar fields. The primary card binds to the scalar fields (which
  // reflect businesses[0]); index >= 1 are the additional businesses rendered below.
  const scheduleCBusinesses = getScheduleCBusinesses(taxReturn);

  // Additional appointments = linked appointments minus the primary, so the primary can never render
  // both as "Primary" and in the "Additional Appointments" list (defensive against stale/dup link data).
  const additionalAppointmentIds = (taxReturn.linkedAppointmentIds ?? []).filter(
    (apptId) => apptId !== taxReturn.appointmentId
  );

  const updateBusinesses = (next: ScheduleCBusiness[]) =>
    onUpdateTaxReturn?.({ scheduleCBusinesses: next } as Partial<TaxReturn>);

  // Apply a patch to a business by index (creating the primary if the return has none yet).
  const patchBusinessAt = (index: number, patch: Partial<ScheduleCBusiness>) => {
    const list = getScheduleCBusinesses(taxReturn).map((b) => ({ ...b }));
    if (list.length === 0) list.push({ id: null, name: null, ein: null, phone: null });
    const target = Math.min(Math.max(index, 0), list.length - 1);
    list[target] = { ...list[target], ...patch };
    updateBusinesses(list);
  };

  const handleLinkBusinessAccount = (account: AccountInfo) => {
    patchBusinessAt(0, {
      id: account.accountId,
      name: account.name,
      phone: account.phone || taxReturn.scheduleCBusinessPhone,
    });
    setShowBusinessLinkSearch(false);
    setBusinessLinkSearchQuery('');
    setBusinessLinkSearchResults(null);
  };

  const handleUnlinkBusinessAccount = () => {
    patchBusinessAt(0, { id: null });
  };

  const handleSaveBusinessName = () => {
    const trimmed = businessNameDraft.trim();
    if (!trimmed) return;
    patchBusinessAt(0, { name: trimmed });
    setEditingBusinessName(false);
  };

  const handleSaveBusinessEIN = () => {
    patchBusinessAt(0, { ein: businessEINDraft || null });
    setEditingBusinessEIN(false);
  };

  const handleSaveBusinessPhone = () => {
    patchBusinessAt(0, { phone: businessPhoneDraft || null });
    setEditingBusinessPhone(false);
  };

  // Remove a business by index; default 0 (the primary card's Remove button).
  const handleRemoveBusiness = (index = 0) => {
    updateBusinesses(getScheduleCBusinesses(taxReturn).filter((_, i) => i !== index));
    setConfirmRemoveBusiness(false);
  };

  return (
    <>
      {/* Quick Status Badges Row */}
      <div style={{
        display: 'flex',
        gap: '16px',
        marginBottom: '24px',
        padding: '16px',
        backgroundColor: 'var(--tl-bg-page)',
        borderRadius: '8px',
        flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>{t('overviewTab.badges.appointmentLabel')}</span>
          <Badge
            appearance="filled"
            style={{
              backgroundColor: appointmentTypeColors[taxReturn.appointmentType as AppointmentType] || 'var(--tl-text-tertiary)',
              color: 'white',
            }}
          >
            {appointmentTypeDisplayNames[taxReturn.appointmentType as AppointmentType] || taxReturn.appointmentType}
          </Badge>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>{t('overviewTab.badges.efileLabel')}</span>
          <Badge
            appearance="filled"
            style={{
              backgroundColor: efileStatusColors[taxReturn.efileStatus] || 'var(--tl-text-tertiary)',
              color: 'white',
            }}
          >
            {efileStatusLabels[taxReturn.efileStatus] || efileStatusLabels.pending}
          </Badge>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>{t('overviewTab.badges.signatureLabel')}</span>
          <Badge
            appearance="filled"
            style={{
              backgroundColor: taxReturn.signatureStatus === 'signed' ? 'var(--tl-success)' :
                              taxReturn.signatureStatus === 'sent' ? 'var(--tl-brand)' : 'var(--tl-text-tertiary)',
              color: 'white',
            }}
          >
            {taxReturn.appointmentType === 'in-person' && taxReturn.signatureStatus === 'pending'
              ? t('overviewTab.signatureStatusLabels.signOnSite')
              : (signatureStatusLabels[taxReturn.signatureStatus] || signatureStatusLabels.pending)}
          </Badge>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>{t('overviewTab.badges.billingLabel')}</span>
          <Badge
            appearance="filled"
            style={{
              backgroundColor: taxReturn.clientInvoice?.status === 'paid' ? 'var(--tl-success)' :
                              taxReturn.clientInvoice?.status === 'generated' ? 'var(--tl-brand)' : 'var(--tl-text-tertiary)',
              color: 'white',
            }}
          >
            {taxReturn.clientInvoice?.status === 'paid' ? t('overviewTab.billing.paid') :
             taxReturn.clientInvoice?.status === 'generated' ? t('overviewTab.billing.invoiced') : t('overviewTab.billing.notInvoiced')}
          </Badge>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>{t('overviewTab.badges.reviewLabel')}</span>
          <Dropdown
            value={REVIEW_STATUS_DISPLAY_NAMES[taxReturn.reviewStatus || 'not_started'] || 'Not Started'}
            selectedOptions={[taxReturn.reviewStatus || 'not_started']}
            onOptionSelect={(_, data) => {
              if (data.optionValue) {
                onReviewStatusChange(data.optionValue as ReviewStatus);
              }
            }}
            style={{ minWidth: '200px' }}
            disabled={reviewStatusUpdating}
            size="small"
          >
            {reviewStatusOptions.map((status) => (
              <Option key={status} text={REVIEW_STATUS_DISPLAY_NAMES[status]} value={status}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: REVIEW_STATUS_COLORS[status],
                  }} />
                  {REVIEW_STATUS_DISPLAY_NAMES[status]}
                </div>
              </Option>
            ))}
          </Dropdown>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>{t('overviewTab.badges.documentsLabel')}</span>
          {taxReturn.allDocumentsReceived ? (
            <Badge appearance="filled" style={{ backgroundColor: '#107c10', color: 'white' }}>
              <CheckmarkCircleRegular /> {t('overviewTab.badges.documentsComplete')}
            </Badge>
          ) : (
            <Badge appearance="filled" style={{ backgroundColor: '#f59e0b', color: 'white' }}>
              <ClockRegular /> {t('overviewTab.badges.documentsPending')}
            </Badge>
          )}
        </div>
        {/* Document Reminder Badge */}
        {taxReturn.status === 'documents_pending' && (taxReturn.documentReminderCount ?? 0) > 0 && (
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '4px 12px',
            backgroundColor: 'var(--tl-warning-bg)',
            border: '1px solid #fed7aa',
            borderRadius: '12px',
            fontSize: tokens.fontSizeBase200,
            color: 'var(--tl-warning)',
          }}>
            <ClockRegular style={{ fontSize: tokens.fontSizeBase300 }} />
            {t('overviewTab.badges.reminderSent', { count: taxReturn.documentReminderCount })}
            {taxReturn.documentReminderLastSentAt && (
              <span> {t('overviewTab.badges.daysAgo', { days: Math.floor((Date.now() - new Date(taxReturn.documentReminderLastSentAt).getTime()) / (1000 * 60 * 60 * 24)) })}</span>
            )}
          </div>
        )}
        {/* E-File Rejection Reminder Badge */}
        {taxReturn.efileStatus === 'rejected' && (taxReturn.efileRejectionReminderCount ?? 0) > 0 && (
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '4px 12px',
            backgroundColor: 'var(--tl-danger-bg)',
            border: '1px solid #fecaca',
            borderRadius: '12px',
            fontSize: tokens.fontSizeBase200,
            color: 'var(--tl-danger)',
          }}>
            <ClockRegular style={{ fontSize: tokens.fontSizeBase300 }} />
            {t('overviewTab.badges.rejectionReminderSent', { count: taxReturn.efileRejectionReminderCount })}
            {taxReturn.efileRejectionReminderLastSentAt && (
              <span> {t('overviewTab.badges.daysAgo', { days: Math.floor((Date.now() - new Date(taxReturn.efileRejectionReminderLastSentAt).getTime()) / (1000 * 60 * 60 * 24)) })}</span>
            )}
          </div>
        )}
        {/* Mark Inactive Button - only show if not already inactive */}
        {taxReturn.status !== 'inactive' && (
          <div style={{ marginLeft: 'auto' }}>
            <Button
              appearance="subtle"
              icon={<DismissCircleRegular />}
              size="small"
              onClick={onMarkInactiveOpen}
            >
              {t('overviewTab.badges.markInactive')}
            </Button>
          </div>
        )}
      </div>

      {/* IRS Letters & Notices — a fact ABOUT this return, so it lives on Overview rather than in
          a tab of its own. Placed above the detail grid because a notice carries a DEADLINE and the
          fields below it do not.

          Rendered only when there is something to say: notices exist, or the fetch failed. A
          measured zero prints nothing, because "no IRS letters" is the normal state of most returns
          and a permanent empty card is noise that trains people to stop reading the region. */}
      {(irsNoticesFailed || (irsNotices && irsNotices.length > 0)) && (
        <div className="detail-section" style={{ marginBottom: '16px' }}>
          <h3>
            <MailRegular /> {t('overviewTab.irsNotices.heading')}
            {openIrsNotices.length > 0 && (
              <Badge appearance="filled" color="danger" size="small" style={{ marginLeft: '8px' }}>
                {t('overviewTab.irsNotices.openCount', { count: openIrsNotices.length })}
              </Badge>
            )}
          </h3>

          {irsNoticesFailed ? (
            // Stated, not swallowed. The reader needs to know this region is unreliable right now;
            // rendering an empty card would assert "no notices" on evidence nobody has.
            <div style={{ color: '#EF4444' }}>
              {t('overviewTab.irsNotices.loadFailed')}
            </div>
          ) : (
            (irsNotices ?? []).map((n) => {
              const bucket = dueBucket(daysUntilDue(n.responseDueDate, new Date()));
              return (
                <div
                  key={n.id}
                  className="detail-row"
                  style={{ alignItems: 'center', gap: '12px' }}
                >
                  <span className="detail-label" style={{ minWidth: '120px' }}>
                    {n.noticeNumber || t('overviewTab.irsNotices.unnumbered')}
                  </span>
                  <span
                    className="detail-value"
                    style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}
                  >
                    <Badge
                      appearance="filled"
                      style={{
                        backgroundColor: irsNoticeStatusColors[n.status],
                        color: 'white',
                      }}
                    >
                      {irsNoticeStatusDisplayNames[n.status] || n.status}
                    </Badge>
                    <span
                      style={{
                        color: bucket === 'overdue' ? '#EF4444' : 'var(--tl-text-secondary)',
                        fontWeight: bucket === 'overdue' ? 600 : 400,
                      }}
                    >
                      {formatDueLabel(n.responseDueDate, new Date())}
                    </span>
                    {n.assignedToName ? (
                      <span style={{ color: 'var(--tl-text-secondary)' }}>{n.assignedToName}</span>
                    ) : (
                      <span style={{ color: 'var(--tl-text-secondary)', fontStyle: 'italic' }}>
                        {t('overviewTab.irsNotices.unassigned')}
                      </span>
                    )}
                  </span>
                </div>
              );
            })
          )}

          {/* Deep link rather than an inline editor: the panel resolves each linked amendment's
              status live and enforces G5, and duplicating that here would be a second, weaker copy
              of the guard. */}
          <div style={{ marginTop: '8px' }}>
            <Link to="/irs-notices">{t('overviewTab.irsNotices.openQueueLink')}</Link>
          </div>
        </div>
      )}

      <div className="detail-grid">
        {/* Client Information */}
        <div className="detail-section">
          <h3><PersonRegular /> {t('overviewTab.clientInfo.heading')}</h3>
          <div className="detail-row">
            <span className="detail-label">{t('overviewTab.clientInfo.nameLabel')}</span>
            <span className="detail-value" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {editingClientName ? (
                <>
                  <Input
                    size="small"
                    value={clientNameDraft}
                    onChange={(_, data) => setClientNameDraft(data.value)}
                    placeholder={t('overviewTab.clientInfo.namePlaceholder')}
                    contentBefore={<PersonRegular />}
                    style={{ flex: 1, maxWidth: '260px' }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSaveClientName(); if (e.key === 'Escape') setEditingClientName(false); }}
                  />
                  <Button
                    appearance="subtle"
                    icon={<CheckmarkRegular />}
                    size="small"
                    onClick={handleSaveClientName}
                    title={t('overviewTab.common.save')}
                  />
                  <Button
                    appearance="subtle"
                    icon={<DismissCircleRegular />}
                    size="small"
                    onClick={() => setEditingClientName(false)}
                    title={t('common.cancel')}
                  />
                </>
              ) : (
                <>
                  {taxReturn.clientName}
                  <Button
                    appearance="subtle"
                    icon={<EditRegular />}
                    size="small"
                    onClick={() => { setClientNameDraft(taxReturn.clientName || ''); setEditingClientName(true); }}
                    title={t('overviewTab.clientInfo.editNameTitle')}
                  />
                  {taxReturn.contactId && (
                    <Badge
                      appearance="filled"
                      size="small"
                      style={{ backgroundColor: '#107c10' }}
                      icon={<LinkRegular />}
                    >
                      {t('overviewTab.clientInfo.linkedBadge')}
                    </Badge>
                  )}
                </>
              )}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">{t('overviewTab.clientInfo.emailLabel')}</span>
            <span className="detail-value" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {editingClientEmail ? (
                <>
                  <Input
                    size="small"
                    type="email"
                    value={clientEmailDraft}
                    onChange={(_, data) => setClientEmailDraft(data.value)}
                    placeholder={t('overviewTab.clientInfo.emailPlaceholder')}
                    contentBefore={<MailRegular />}
                    style={{ flex: 1, maxWidth: '260px' }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSaveClientEmail(); if (e.key === 'Escape') setEditingClientEmail(false); }}
                  />
                  <Button
                    appearance="subtle"
                    icon={<CheckmarkRegular />}
                    size="small"
                    onClick={handleSaveClientEmail}
                    title={t('overviewTab.common.save')}
                  />
                  <Button
                    appearance="subtle"
                    icon={<DismissCircleRegular />}
                    size="small"
                    onClick={() => setEditingClientEmail(false)}
                    title={t('common.cancel')}
                  />
                </>
              ) : (
                <>
                  {taxReturn.clientEmail || '-'}
                  <Button
                    appearance="subtle"
                    icon={<EditRegular />}
                    size="small"
                    onClick={() => { setClientEmailDraft(taxReturn.clientEmail || ''); setEditingClientEmail(true); }}
                    title={t('overviewTab.clientInfo.editEmailTitle')}
                  />
                </>
              )}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">{t('overviewTab.clientInfo.phoneLabel')}</span>
            <span className="detail-value" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {editingClientPhone ? (
                <>
                  <Input
                    size="small"
                    value={clientPhoneDraft}
                    onChange={(_, data) => setClientPhoneDraft(data.value)}
                    placeholder={t('overviewTab.clientInfo.phonePlaceholder')}
                    contentBefore={<PhoneRegular />}
                    style={{ flex: 1, maxWidth: '260px' }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSaveClientPhone(); if (e.key === 'Escape') setEditingClientPhone(false); }}
                  />
                  <Button
                    appearance="subtle"
                    icon={<CheckmarkRegular />}
                    size="small"
                    onClick={handleSaveClientPhone}
                    title={t('overviewTab.common.save')}
                  />
                  <Button
                    appearance="subtle"
                    icon={<DismissCircleRegular />}
                    size="small"
                    onClick={() => setEditingClientPhone(false)}
                    title={t('common.cancel')}
                  />
                </>
              ) : (
                <>
                  {taxReturn.clientPhone || '-'}
                  <Button
                    appearance="subtle"
                    icon={<EditRegular />}
                    size="small"
                    onClick={() => { setClientPhoneDraft(taxReturn.clientPhone || ''); setEditingClientPhone(true); }}
                    title={t('overviewTab.clientInfo.editPhoneTitle')}
                  />
                </>
              )}
            </span>
          </div>
          {taxReturn.contactName && (
            <div className="detail-row">
              <span className="detail-label">{t('overviewTab.clientInfo.dataverseContactLabel')}</span>
              <span className="detail-value">{taxReturn.contactName}</span>
            </div>
          )}
          {/* Link / Unlink to Dataverse */}
          <div style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'center' }}>
            {taxReturn.contactId ? (
              <Button
                appearance="subtle"
                icon={<DismissCircleRegular />}
                size="small"
                onClick={() => {
                  if (confirm(t('overviewTab.clientInfo.unlinkConfirm'))) {
                    handleUnlinkContact();
                  }
                }}
              >
                {t('overviewTab.clientInfo.unlinkButton')}
              </Button>
            ) : (
              <Button
                appearance="subtle"
                icon={<LinkRegular />}
                size="small"
                onClick={() => setShowLinkSearch(!showLinkSearch)}
              >
                {t('overviewTab.clientInfo.linkButton')}
              </Button>
            )}
          </div>
          {/* Dataverse Link Search */}
          {/*
            S2 Q3 — the confirmation. Counts, at their stated grains, before anything moves.
            Allowed at any return status by ruling: refusing above a threshold would refuse the
            exact case relink exists for, and there is no support path to refuse it into.
          */}
          <Dialog open={pendingRelink !== null} onOpenChange={(_, d) => { if (!d.open) setPendingRelink(null); }}>
            <DialogSurface>
              <DialogBody>
                <DialogTitle>{t('overviewTab.relinkDialog.title')}</DialogTitle>
                <DialogContent>
                  <p style={{ marginTop: 0 }}>
                    <Trans
                      i18nKey="overviewTab.relinkDialog.body"
                      values={{ clientName: pendingRelink?.clientName }}
                      components={{ strong: <strong /> }}
                    />
                  </p>
                  {relinkConsequence && (
                    <p style={{
                      padding: '8px 12px',
                      borderRadius: '6px',
                      backgroundColor: 'var(--tl-warn-bg, #fff4ce)',
                      border: '1px solid var(--tl-warn-border, #e9c46a)',
                    }}>
                      {relinkConsequence}
                    </p>
                  )}
                  <p style={{ marginBottom: 0, fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)' }}>
                    {t('overviewTab.relinkDialog.footnote')}
                  </p>
                </DialogContent>
                <DialogActions>
                  <Button appearance="secondary" onClick={() => setPendingRelink(null)}>{t('common.cancel')}</Button>
                  <Button
                    appearance="primary"
                    onClick={() => pendingRelink && applyRelink(pendingRelink)}
                  >
                    {t('overviewTab.relinkDialog.confirmButton', { clientName: pendingRelink?.clientName })}
                  </Button>
                </DialogActions>
              </DialogBody>
            </DialogSurface>
          </Dialog>

          {showLinkSearch && (
            <div style={{
              marginTop: '12px',
              padding: '12px',
              backgroundColor: 'var(--tl-bg-page)',
              borderRadius: '8px',
              border: '1px solid var(--tl-border)',
            }}>
              {/*
                S2 Q5 — say what a relink MEANS, before one is picked.
                Relinking is forward-only: this return starts pointing at the new client, and
                everything already attached to the old one stays there. That is deliberate —
                an invoice that was issued and paid under the old client really was that
                client's, and moving it would rewrite history.
                Phrased about where the history STAYS rather than about anything being
                recorded, because that sentence is true of the app today and stays true once
                previousClientId starts populating.
                This is the anti-dead-dialog: the prompt this replaces described what it did
                and never what it meant.
              */}
              <div style={{
                marginBottom: '12px',
                padding: '8px 12px',
                borderRadius: '6px',
                backgroundColor: 'var(--tl-warn-bg, #fff4ce)',
                border: '1px solid var(--tl-warn-border, #e9c46a)',
                fontSize: tokens.fontSizeBase200,
                lineHeight: 1.45,
              }}>
                <Trans
                  i18nKey="overviewTab.linkSearch.warning"
                  values={{ clientName: taxReturn.clientName || t('overviewTab.linkSearch.currentClientFallback') }}
                  components={{ strong: <strong /> }}
                />
              </div>
              <Input
                value={linkSearchQuery}
                onChange={(_, data) => setLinkSearchQuery(data.value)}
                placeholder={t('overviewTab.linkSearch.placeholder')}
                contentBefore={<SearchRegular />}
                contentAfter={isLinkSearching ? <Spinner size="tiny" /> : undefined}
                style={{ width: '100%', marginBottom: '8px' }}
              />
              {linkSearchResults && (linkSearchResults.contacts.length > 0 || linkSearchResults.accounts.length > 0) && (
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {linkSearchResults.contacts.map((contact) => (
                    <div
                      key={`contact-${contact.contactId}`}
                      style={{
                        padding: '8px',
                        cursor: 'pointer',
                        borderBottom: '1px solid var(--tl-bg-subtle)',
                        borderRadius: '4px',
                      }}
                      onClick={() => handleLinkContact(contact)}
                      onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'var(--tl-brand-bg)')}
                      onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <PersonRegular style={{ fontSize: tokens.fontSizeBase300, color: 'var(--tl-brand)' }} />
                        <span style={{ fontWeight: 500, fontSize: '13px' }}>{contact.fullName}</span>
                      </div>
                      <div style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)', marginLeft: '20px' }}>
                        {[contact.email, contact.phone].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                  ))}
                  {linkSearchResults.accounts.map((account) => (
                    <div
                      key={`account-${account.accountId}`}
                      style={{
                        padding: '8px',
                        cursor: 'pointer',
                        borderBottom: '1px solid var(--tl-bg-subtle)',
                        borderRadius: '4px',
                      }}
                      onClick={() => handleLinkAccount(account)}
                      onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'var(--tl-brand-bg)')}
                      onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <BuildingRegular style={{ fontSize: tokens.fontSizeBase300, color: 'var(--tl-success)' }} />
                        <span style={{ fontWeight: 500, fontSize: '13px' }}>{account.name}</span>
                        <Badge appearance="outline" size="small">{t('overviewTab.linkSearch.accountBadge')}</Badge>
                      </div>
                      <div style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)', marginLeft: '20px' }}>
                        {[account.email, account.phone].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {linkSearchResults && linkSearchResults.contacts.length === 0 && linkSearchResults.accounts.length === 0 && linkSearchQuery.trim().length >= 2 && (
                <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)', fontStyle: 'italic' }}>
                  {t('overviewTab.linkSearch.noResults')}
                </div>
              )}
            </div>
          )}
          {/* Searchable aliases */}
          {taxReturn.searchableAliases && taxReturn.searchableAliases.length > 0 && (
            <div className="detail-row">
              <span className="detail-label">{t('overviewTab.clientInfo.aliasesLabel')}</span>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {taxReturn.searchableAliases.map((alias, idx) => (
                  <Badge key={idx} appearance="outline" size="small">
                    {alias}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          {/* Add Spouse / Merge Return as Spouse — offered whenever no spouse is RECORDED.
              Deliberately NOT gated on filingType: a return that was already joint but had
              no spouse used to fall between this guard and the Spouse Information block
              below (which needs spouseName), leaving no way at all to record a spouse.
              See spouseAffordance.ts for the incident this prevents. */}
          {canRecordSpouse(taxReturn) && (
            <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
              <Button
                appearance="subtle"
                icon={<PeopleRegular />}
                size="small"
                onClick={onAddSpouseOpen}
              >
                {t('overviewTab.clientInfo.addSpouse')}
              </Button>
              <Button
                appearance="subtle"
                icon={<PeopleCommunityRegular />}
                size="small"
                onClick={onMergeSpouseOpen}
              >
                {t('overviewTab.clientInfo.mergeSpouse')}
              </Button>
            </div>
          )}
          {/* Add Schedule C Business buttons - only for individual returns without a business */}
          {taxReturn.returnType === 'individual' && !taxReturn.scheduleCBusinessName && (
            <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
              <Button
                appearance="subtle"
                icon={<BuildingRegular />}
                size="small"
                onClick={onAddBusinessOpen}
              >
                {t('overviewTab.clientInfo.addBusiness')}
              </Button>
              <Button
                appearance="subtle"
                icon={<PeopleCommunityRegular />}
                size="small"
                onClick={onMergeScheduleCOpen}
              >
                {t('overviewTab.clientInfo.mergeBusiness')}
              </Button>
            </div>
          )}
          {/* View in Dataverse button */}
          {isDataverseConfigured() && taxReturn.clientId && (
            <div style={{ marginTop: '12px' }}>
              <Button
                as="a"
                appearance="subtle"
                icon={<OpenRegular />}
                size="small"
                href={getDataverseRecordUrl(
                  taxReturn.returnType === 'business' ? 'account' : 'contact',
                  taxReturn.clientId
                ) ?? undefined}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('overviewTab.clientInfo.viewInDataverse')}
              </Button>
            </div>
          )}
          {/* Bookkeeping Client Status */}
          {bookkeepingStatus?.isBookkeepingClient && (
            <div style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: 'var(--tl-brand-bg)',
              borderRadius: '8px',
              border: '1px solid #0078d4',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <Badge
                  appearance="filled"
                  style={{ backgroundColor: '#0078d4' }}
                >
                  {t('overviewTab.bookkeeping.badge')}
                </Badge>
              </div>
              {bookkeepingStatus.latestReview && (
                <div>
                  <div className="detail-row" style={{ margin: '4px 0' }}>
                    <span className="detail-label" style={{ fontSize: '13px' }}>{t('overviewTab.bookkeeping.latestReviewLabel')}</span>
                    <span className="detail-value" style={{ fontSize: '13px' }}>
                      {bookkeepingStatus.latestReview.reviewPeriod ||
                       (bookkeepingStatus.latestReview.reviewYear && bookkeepingStatus.latestReview.reviewMonth
                         ? `${bookkeepingStatus.latestReview.reviewYear}-${String(bookkeepingStatus.latestReview.reviewMonth).padStart(2, '0')}`
                         : bookkeepingStatus.latestReview.reviewYear || t('overviewTab.bookkeeping.naFallback'))}
                    </span>
                  </div>
                  <div className="detail-row" style={{ margin: '4px 0' }}>
                    <span className="detail-label" style={{ fontSize: '13px' }}>{t('overviewTab.bookkeeping.reviewStatusLabel')}</span>
                    <Badge
                      appearance="filled"
                      size="small"
                      style={{
                        backgroundColor: bookkeepingStatus.latestReview.reviewCompleted ||
                                         bookkeepingStatus.latestReview.status === 'completed' ||
                                         bookkeepingStatus.latestReview.status === 'complete'
                          ? 'var(--tl-success)'
                          : 'var(--tl-warning)',
                      }}
                    >
                      {bookkeepingStatus.latestReview.reviewCompleted ||
                       bookkeepingStatus.latestReview.status === 'completed' ||
                       bookkeepingStatus.latestReview.status === 'complete'
                        ? t('overviewTab.bookkeeping.completed')
                        : bookkeepingStatus.latestReview.status || t('overviewTab.bookkeeping.pendingFallback')}
                    </Badge>
                  </div>
                  {bookkeepingStatus.latestReview.reviewCompletedDate && (
                    <div className="detail-row" style={{ margin: '4px 0' }}>
                      <span className="detail-label" style={{ fontSize: '13px' }}>{t('overviewTab.bookkeeping.completedDateLabel')}</span>
                      <span className="detail-value" style={{ fontSize: '13px' }}>
                        {format(new Date(bookkeepingStatus.latestReview.reviewCompletedDate), 'MMM d, yyyy')}
                      </span>
                    </div>
                  )}
                </div>
              )}
              {bookkeepingStatus.allReviews.length > 1 && (
                <div style={{ marginTop: '8px', fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)' }}>
                  {t('overviewTab.bookkeeping.reviewsCompletedCount', {
                    completed: bookkeepingStatus.reviewsCompleted,
                    total: bookkeepingStatus.allReviews.length,
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Spouse Information - Only show for joint filers */}
        {taxReturn.filingType === 'joint' && taxReturn.spouseName && (
          <div className="detail-section">
            <h3><PeopleRegular /> {t('overviewTab.spouseInfo.heading')}</h3>
            <div className="detail-row">
              <span className="detail-label">{t('overviewTab.clientInfo.nameLabel')}</span>
              <span className="detail-value" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {editingSpouseName ? (
                  <>
                    <Input
                      size="small"
                      value={spouseNameDraft}
                      onChange={(_, data) => setSpouseNameDraft(data.value)}
                      placeholder={t('overviewTab.spouseInfo.namePlaceholder')}
                      contentBefore={<PersonRegular />}
                      style={{ flex: 1, maxWidth: '260px' }}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSaveSpouseName(); if (e.key === 'Escape') setEditingSpouseName(false); }}
                    />
                    <Button
                      appearance="subtle"
                      icon={<CheckmarkRegular />}
                      size="small"
                      onClick={handleSaveSpouseName}
                      title={t('overviewTab.common.save')}
                    />
                    <Button
                      appearance="subtle"
                      icon={<DismissCircleRegular />}
                      size="small"
                      onClick={() => setEditingSpouseName(false)}
                      title={t('common.cancel')}
                    />
                  </>
                ) : (
                  <>
                    {taxReturn.spouseName}
                    <Button
                      appearance="subtle"
                      icon={<EditRegular />}
                      size="small"
                      onClick={() => { setSpouseNameDraft(taxReturn.spouseName || ''); setEditingSpouseName(true); }}
                      title={t('overviewTab.clientInfo.editNameTitle')}
                    />
                    {taxReturn.spouseId && (
                      <Badge
                        appearance="filled"
                        size="small"
                        style={{ backgroundColor: '#107c10' }}
                        icon={<LinkRegular />}
                      >
                        {t('overviewTab.clientInfo.linkedBadge')}
                      </Badge>
                    )}
                  </>
                )}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">{t('overviewTab.clientInfo.emailLabel')}</span>
              <span className="detail-value" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {editingSpouseEmail ? (
                  <>
                    <Input
                      size="small"
                      type="email"
                      value={spouseEmailDraft}
                      onChange={(_, data) => setSpouseEmailDraft(data.value)}
                      placeholder={t('overviewTab.spouseInfo.emailPlaceholder')}
                      contentBefore={<MailRegular />}
                      style={{ flex: 1, maxWidth: '260px' }}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSaveSpouseEmail(); if (e.key === 'Escape') setEditingSpouseEmail(false); }}
                    />
                    <Button
                      appearance="subtle"
                      icon={<CheckmarkRegular />}
                      size="small"
                      onClick={handleSaveSpouseEmail}
                      title={t('overviewTab.common.save')}
                    />
                    <Button
                      appearance="subtle"
                      icon={<DismissCircleRegular />}
                      size="small"
                      onClick={() => setEditingSpouseEmail(false)}
                      title={t('common.cancel')}
                    />
                  </>
                ) : (
                  <>
                    {taxReturn.spouseEmail || <span style={{ color: 'var(--tl-danger)', fontStyle: 'italic' }}>{t('overviewTab.spouseInfo.emailNotSet')}</span>}
                    <Button
                      appearance="subtle"
                      icon={<EditRegular />}
                      size="small"
                      onClick={() => { setSpouseEmailDraft(taxReturn.spouseEmail || ''); setEditingSpouseEmail(true); }}
                      title={t('overviewTab.clientInfo.editEmailTitle')}
                    />
                  </>
                )}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">{t('overviewTab.clientInfo.phoneLabel')}</span>
              <span className="detail-value" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {editingSpousePhone ? (
                  <>
                    <Input
                      size="small"
                      value={spousePhoneDraft}
                      onChange={(_, data) => setSpousePhoneDraft(data.value)}
                      placeholder={t('overviewTab.spouseInfo.phonePlaceholder')}
                      contentBefore={<PhoneRegular />}
                      style={{ flex: 1, maxWidth: '260px' }}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSaveSpousePhone(); if (e.key === 'Escape') setEditingSpousePhone(false); }}
                    />
                    <Button
                      appearance="subtle"
                      icon={<CheckmarkRegular />}
                      size="small"
                      onClick={handleSaveSpousePhone}
                      title={t('overviewTab.common.save')}
                    />
                    <Button
                      appearance="subtle"
                      icon={<DismissCircleRegular />}
                      size="small"
                      onClick={() => setEditingSpousePhone(false)}
                      title={t('common.cancel')}
                    />
                  </>
                ) : (
                  <>
                    {taxReturn.spousePhone || '-'}
                    <Button
                      appearance="subtle"
                      icon={<EditRegular />}
                      size="small"
                      onClick={() => { setSpousePhoneDraft(taxReturn.spousePhone || ''); setEditingSpousePhone(true); }}
                      title={t('overviewTab.clientInfo.editPhoneTitle')}
                    />
                  </>
                )}
              </span>
            </div>
            {/* Link / Unlink Spouse to Dataverse */}
            <div style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'center' }}>
              {taxReturn.spouseId ? (
                <>
                  <Button
                    appearance="subtle"
                    icon={<DismissCircleRegular />}
                    size="small"
                    onClick={() => {
                      if (confirm(t('overviewTab.spouseInfo.unlinkConfirm'))) {
                        handleUnlinkSpouseContact();
                      }
                    }}
                  >
                    {t('overviewTab.clientInfo.unlinkButton')}
                  </Button>
                  {isDataverseConfigured() && (
                    <Button
                      as="a"
                      appearance="subtle"
                      icon={<OpenRegular />}
                      size="small"
                      href={getDataverseRecordUrl('contact', taxReturn.spouseId!) ?? undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('overviewTab.clientInfo.viewInDataverse')}
                    </Button>
                  )}
                </>
              ) : (
                <Button
                  appearance="subtle"
                  icon={<LinkRegular />}
                  size="small"
                  onClick={() => setShowSpouseLinkSearch(!showSpouseLinkSearch)}
                >
                  {t('overviewTab.spouseInfo.linkButton')}
                </Button>
              )}
            </div>
            {/* Remove Spouse / Swap Taxpayer & Spouse */}
            <div style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              {!confirmRemoveSpouse ? (
                <Button
                  appearance="subtle"
                  icon={<DeleteRegular />}
                  size="small"
                  onClick={() => setConfirmRemoveSpouse(true)}
                  style={{ color: 'var(--tl-danger)' }}
                >
                  {t('overviewTab.spouseInfo.removeSpouse')}
                </Button>
              ) : (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  backgroundColor: 'var(--tl-danger-bg)',
                  borderRadius: '6px',
                  border: '1px solid #d83b01',
                }}>
                  <span style={{ fontSize: '13px', color: 'var(--tl-danger)' }}>
                    {t('overviewTab.spouseInfo.removeSpouseConfirm')}
                  </span>
                  <Button
                    appearance="primary"
                    size="small"
                    onClick={handleRemoveSpouse}
                    style={{ backgroundColor: '#d83b01', borderColor: 'var(--tl-danger)' }}
                  >
                    {t('overviewTab.spouseInfo.yesRemove')}
                  </Button>
                  <Button
                    appearance="subtle"
                    size="small"
                    onClick={() => setConfirmRemoveSpouse(false)}
                  >
                    {t('common.cancel')}
                  </Button>
                </div>
              )}
              {!confirmSwapTaxpayer ? (
                <Button
                  appearance="subtle"
                  icon={<ArrowSwapRegular />}
                  size="small"
                  onClick={() => setConfirmSwapTaxpayer(true)}
                >
                  {t('overviewTab.spouseInfo.swapTaxpayer')}
                </Button>
              ) : (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  backgroundColor: 'var(--tl-brand-bg)',
                  borderRadius: '6px',
                  border: '1px solid #0078d4',
                }}>
                  <span style={{ fontSize: '13px', color: 'var(--tl-brand)' }}>
                    <Trans
                      i18nKey="overviewTab.spouseInfo.swapConfirmText"
                      values={{ spouseName: taxReturn.spouseName }}
                      components={{ strong: <strong /> }}
                    />
                  </span>
                  <Button
                    appearance="primary"
                    size="small"
                    onClick={() => { onSwapTaxpayerSpouse(); setConfirmSwapTaxpayer(false); }}
                    disabled={swapTaxpayerSpousePending}
                  >
                    {swapTaxpayerSpousePending ? t('overviewTab.spouseInfo.swapping') : t('overviewTab.spouseInfo.yesSwap')}
                  </Button>
                  <Button
                    appearance="subtle"
                    size="small"
                    onClick={() => setConfirmSwapTaxpayer(false)}
                  >
                    {t('common.cancel')}
                  </Button>
                </div>
              )}
            </div>
            {/* Spouse Dataverse Link Search */}
            {showSpouseLinkSearch && (
              <div style={{
                marginTop: '12px',
                padding: '12px',
                backgroundColor: 'var(--tl-bg-page)',
                borderRadius: '8px',
                border: '1px solid var(--tl-border)',
              }}>
                <Input
                  value={spouseLinkSearchQuery}
                  onChange={(_, data) => setSpouseLinkSearchQuery(data.value)}
                  placeholder={t('overviewTab.spouseInfo.searchPlaceholder')}
                  contentBefore={<SearchRegular />}
                  contentAfter={isSpouseLinkSearching ? <Spinner size="tiny" /> : undefined}
                  style={{ width: '100%', marginBottom: '8px' }}
                />
                {spouseLinkSearchResults && spouseLinkSearchResults.contacts.length > 0 && (
                  <div style={{ maxHeight: '160px', overflowY: 'auto' }}>
                    {spouseLinkSearchResults.contacts.map((contact) => (
                      <div
                        key={contact.contactId}
                        style={{
                          padding: '8px',
                          cursor: 'pointer',
                          borderBottom: '1px solid var(--tl-bg-subtle)',
                          borderRadius: '4px',
                        }}
                        onClick={() => handleLinkSpouseContact(contact)}
                        onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'var(--tl-brand-bg)')}
                        onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                      >
                        <div style={{ fontWeight: 500, fontSize: '13px' }}>{contact.fullName}</div>
                        <div style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)' }}>
                          {[contact.email, contact.phone].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {spouseLinkSearchResults && spouseLinkSearchResults.contacts.length === 0 && spouseLinkSearchQuery.trim().length >= 2 && (
                  <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)', fontStyle: 'italic' }}>
                    {t('overviewTab.spouseInfo.noResults')}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Schedule C Business Information - Only show for individual returns with a business */}
        {taxReturn.returnType === 'individual' && taxReturn.scheduleCBusinessName && (
          <div className="detail-section">
            <h3><BuildingRegular /> {t('overviewTab.businessInfo.heading')}</h3>
            <div className="detail-row">
              <span className="detail-label">{t('overviewTab.businessInfo.businessNameLabel')}</span>
              <span className="detail-value" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {editingBusinessName ? (
                  <>
                    <Input
                      size="small"
                      value={businessNameDraft}
                      onChange={(_, data) => setBusinessNameDraft(data.value)}
                      placeholder={t('overviewTab.businessInfo.namePlaceholder')}
                      contentBefore={<BuildingRegular />}
                      style={{ flex: 1, maxWidth: '260px' }}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSaveBusinessName(); if (e.key === 'Escape') setEditingBusinessName(false); }}
                    />
                    <Button
                      appearance="subtle"
                      icon={<CheckmarkRegular />}
                      size="small"
                      onClick={handleSaveBusinessName}
                      title={t('overviewTab.common.save')}
                    />
                    <Button
                      appearance="subtle"
                      icon={<DismissCircleRegular />}
                      size="small"
                      onClick={() => setEditingBusinessName(false)}
                      title={t('common.cancel')}
                    />
                  </>
                ) : (
                  <>
                    {taxReturn.scheduleCBusinessName}
                    <Button
                      appearance="subtle"
                      icon={<EditRegular />}
                      size="small"
                      onClick={() => { setBusinessNameDraft(taxReturn.scheduleCBusinessName || ''); setEditingBusinessName(true); }}
                      title={t('overviewTab.businessInfo.editNameTitle')}
                    />
                  </>
                )}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">{t('overviewTab.businessInfo.einLabel')}</span>
              <span className="detail-value" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {editingBusinessEIN ? (
                  <>
                    <Input
                      size="small"
                      value={businessEINDraft}
                      onChange={(_, data) => setBusinessEINDraft(data.value)}
                      placeholder={t('overviewTab.businessInfo.einPlaceholder')}
                      style={{ flex: 1, maxWidth: '200px' }}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSaveBusinessEIN(); if (e.key === 'Escape') setEditingBusinessEIN(false); }}
                    />
                    <Button
                      appearance="subtle"
                      icon={<CheckmarkRegular />}
                      size="small"
                      onClick={handleSaveBusinessEIN}
                      title={t('overviewTab.common.save')}
                    />
                    <Button
                      appearance="subtle"
                      icon={<DismissCircleRegular />}
                      size="small"
                      onClick={() => setEditingBusinessEIN(false)}
                      title={t('common.cancel')}
                    />
                  </>
                ) : (
                  <>
                    {taxReturn.scheduleCBusinessEIN || '-'}
                    <Button
                      appearance="subtle"
                      icon={<EditRegular />}
                      size="small"
                      onClick={() => { setBusinessEINDraft(taxReturn.scheduleCBusinessEIN || ''); setEditingBusinessEIN(true); }}
                      title={t('overviewTab.businessInfo.editEinTitle')}
                    />
                  </>
                )}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">{t('overviewTab.clientInfo.phoneLabel')}</span>
              <span className="detail-value" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {editingBusinessPhone ? (
                  <>
                    <Input
                      size="small"
                      value={businessPhoneDraft}
                      onChange={(_, data) => setBusinessPhoneDraft(data.value)}
                      placeholder={t('overviewTab.businessInfo.phonePlaceholder')}
                      contentBefore={<PhoneRegular />}
                      style={{ flex: 1, maxWidth: '200px' }}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSaveBusinessPhone(); if (e.key === 'Escape') setEditingBusinessPhone(false); }}
                    />
                    <Button
                      appearance="subtle"
                      icon={<CheckmarkRegular />}
                      size="small"
                      onClick={handleSaveBusinessPhone}
                      title={t('overviewTab.common.save')}
                    />
                    <Button
                      appearance="subtle"
                      icon={<DismissCircleRegular />}
                      size="small"
                      onClick={() => setEditingBusinessPhone(false)}
                      title={t('common.cancel')}
                    />
                  </>
                ) : (
                  <>
                    {taxReturn.scheduleCBusinessPhone || '-'}
                    <Button
                      appearance="subtle"
                      icon={<EditRegular />}
                      size="small"
                      onClick={() => { setBusinessPhoneDraft(taxReturn.scheduleCBusinessPhone || ''); setEditingBusinessPhone(true); }}
                      title={t('overviewTab.businessInfo.editPhoneTitle')}
                    />
                  </>
                )}
              </span>
            </div>

            {/* Billing Name */}
            <div className="detail-row" style={{ marginTop: '4px' }}>
              <span className="detail-label">{t('overviewTab.businessInfo.billingNameLabel')}</span>
              <span className="detail-value" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {taxReturn.billingName ? (
                  <>
                    <Badge appearance="filled" size="small" style={{ backgroundColor: '#8B5CF6' }}>
                      {taxReturn.billingName}
                    </Badge>
                    <Button
                      appearance="subtle"
                      icon={<DismissCircleRegular />}
                      size="small"
                      onClick={() => onUpdateTaxReturn?.({ billingName: null } as Partial<TaxReturn>)}
                      title={t('overviewTab.businessInfo.clearBillingNameTitle')}
                      style={{ color: 'var(--tl-danger)' }}
                    />
                  </>
                ) : (
                  <>
                    <span style={{ color: 'var(--tl-text-secondary)', fontSize: '13px' }}>
                      {t('overviewTab.businessInfo.defaultBillingName', { clientName: taxReturn.clientName })}
                    </span>
                    {scheduleCBusinesses.filter((b) => b.name).map((b) => (
                      <Button
                        key={b.id || b.name}
                        appearance="subtle"
                        icon={<BuildingRegular />}
                        size="small"
                        onClick={() => onUpdateTaxReturn?.({ billingName: b.name } as Partial<TaxReturn>)}
                      >
                        {t('overviewTab.businessInfo.billAs', { name: b.name })}
                      </Button>
                    ))}
                  </>
                )}
              </span>
            </div>

            {/* Link / Unlink Business to Dataverse */}
            <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {taxReturn.scheduleCBusinessId ? (
                <>
                  <Badge appearance="filled" color="success" size="small">{t('overviewTab.businessInfo.linkedBadge')}</Badge>
                  <Button
                    appearance="subtle"
                    icon={<DismissCircleRegular />}
                    size="small"
                    onClick={() => {
                      if (confirm(t('overviewTab.businessInfo.unlinkConfirm'))) {
                        handleUnlinkBusinessAccount();
                      }
                    }}
                  >
                    {t('overviewTab.businessInfo.unlinkButton')}
                  </Button>
                  {isDataverseConfigured() && (
                    <Button
                      as="a"
                      appearance="subtle"
                      icon={<OpenRegular />}
                      size="small"
                      href={getDataverseRecordUrl('account', taxReturn.scheduleCBusinessId!) ?? undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('overviewTab.clientInfo.viewInDataverse')}
                    </Button>
                  )}
                </>
              ) : (
                <Button
                  appearance="subtle"
                  icon={<LinkRegular />}
                  size="small"
                  onClick={() => setShowBusinessLinkSearch(!showBusinessLinkSearch)}
                >
                  {t('overviewTab.businessInfo.linkButton')}
                </Button>
              )}
            </div>

            {/* Remove Business */}
            <div style={{ marginTop: '8px' }}>
              {!confirmRemoveBusiness ? (
                <Button
                  appearance="subtle"
                  icon={<DeleteRegular />}
                  size="small"
                  style={{ color: 'var(--tl-danger)' }}
                  onClick={() => setConfirmRemoveBusiness(true)}
                >
                  {t('overviewTab.businessInfo.removeBusiness')}
                </Button>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '13px', color: 'var(--tl-danger)' }}>
                    {t('overviewTab.businessInfo.removeBusinessConfirm')}
                  </span>
                  <Button
                    appearance="primary"
                    size="small"
                    style={{ backgroundColor: '#d83b01' }}
                    onClick={() => handleRemoveBusiness(0)}
                  >
                    {t('overviewTab.businessInfo.confirmButton')}
                  </Button>
                  <Button
                    appearance="subtle"
                    size="small"
                    onClick={() => setConfirmRemoveBusiness(false)}
                  >
                    {t('common.cancel')}
                  </Button>
                </div>
              )}
            </div>

            {/* Business Dataverse Link Search */}
            {showBusinessLinkSearch && (
              <div style={{ marginTop: '12px', padding: '12px', backgroundColor: 'var(--tl-bg-page)', borderRadius: '8px', border: '1px solid var(--tl-border)' }}>
                <Input
                  size="small"
                  value={businessLinkSearchQuery}
                  onChange={(_, data) => setBusinessLinkSearchQuery(data.value)}
                  placeholder={t('overviewTab.businessInfo.searchPlaceholder')}
                  contentBefore={<SearchRegular />}
                  contentAfter={isBusinessLinkSearching ? <Spinner size="tiny" /> : undefined}
                  style={{ width: '100%', marginBottom: '8px' }}
                />
                {businessLinkSearchResults && businessLinkSearchResults.length > 0 && (
                  <div style={{ maxHeight: '150px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {businessLinkSearchResults.map((account) => (
                      <div
                        key={account.accountId}
                        style={{ padding: '8px', backgroundColor: 'white', borderRadius: '4px', border: '1px solid var(--tl-border)', cursor: 'pointer' }}
                        onClick={() => handleLinkBusinessAccount(account)}
                      >
                        <div style={{ fontWeight: 500, fontSize: '13px' }}>{account.name}</div>
                        <div style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)' }}>
                          {[account.phone, account.primaryContactName].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {businessLinkSearchResults && businessLinkSearchResults.length === 0 && businessLinkSearchQuery.trim().length >= 2 && (
                  <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)', fontStyle: 'italic' }}>
                    {t('overviewTab.businessInfo.noResults')}
                  </div>
                )}
              </div>
            )}

            {/* Additional Schedule C businesses (beyond the primary shown above) */}
            {scheduleCBusinesses.length > 1 && (
              <div style={{ marginTop: '12px', borderTop: '1px solid var(--tl-bg-subtle)', paddingTop: '12px' }}>
                <div style={{ fontSize: '13px', fontWeight: 500, marginBottom: '8px' }}>
                  {t('overviewTab.businessInfo.additionalHeading')}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {scheduleCBusinesses.slice(1).map((b, i) => (
                    <div
                      key={b.id || b.name || i}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', backgroundColor: 'var(--tl-bg-page)', borderRadius: '6px', border: '1px solid var(--tl-border)' }}
                    >
                      <BuildingRegular style={{ color: 'var(--tl-success)' }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 500, fontSize: '13px' }}>{b.name || t('overviewTab.businessInfo.unnamed')}</div>
                        <div style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)' }}>
                          {[b.ein ? t('overviewTab.businessInfo.einFormat', { ein: b.ein }) : null, b.phone].filter(Boolean).join(' · ') || '—'}
                        </div>
                      </div>
                      {b.id ? (
                        <Badge appearance="filled" color="success" size="small">{t('overviewTab.businessInfo.linkedBadgeShort')}</Badge>
                      ) : (
                        <Badge appearance="outline" size="small">{t('overviewTab.businessInfo.notLinkedBadge')}</Badge>
                      )}
                      <Button
                        appearance="subtle"
                        icon={<DeleteRegular />}
                        size="small"
                        style={{ color: 'var(--tl-danger)' }}
                        title={t('overviewTab.businessInfo.removeBusinessTitle')}
                        onClick={() => handleRemoveBusiness(i + 1)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          {/* Add another Schedule C business — the primary "Add Schedule C Business" trigger
              (in Client Information) is hidden once a business exists, so this is how staff add a
              2nd/3rd business to the same return. AddBusinessDialog appends to the list. */}
          <div style={{ marginTop: '12px' }}>
            <Button
              appearance="subtle"
              icon={<AddRegular />}
              size="small"
              onClick={onAddBusinessOpen}
            >
              {t('overviewTab.businessInfo.addAnother')}
            </Button>
          </div>
          </div>
        )}

        {/* Appointment Details */}
        <div className="detail-section">
          <h3>
            <CalendarRegular /> {t('overviewTab.appointments.heading')}
            {additionalAppointmentIds.length > 0 && (
              <Badge
                size="small"
                appearance="filled"
                style={{ marginLeft: '8px', backgroundColor: '#0078d4' }}
              >
                {1 + additionalAppointmentIds.length}
              </Badge>
            )}
          </h3>
          {/* Primary Appointment */}
          <div style={{
            padding: '12px',
            backgroundColor: 'var(--tl-bg-subtle)',
            borderRadius: '8px',
            marginBottom: additionalAppointmentIds.length ? '12px' : '0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <Badge size="small" appearance="outline" style={{ color: 'var(--tl-brand)', borderColor: 'var(--tl-brand)' }}>
                {t('overviewTab.appointments.primaryBadge')}
              </Badge>
              <Badge
                size="small"
                appearance="filled"
                style={{
                  backgroundColor: appointmentTypeColors[taxReturn.appointmentType as AppointmentType] || 'var(--tl-text-tertiary)',
                  color: 'white',
                }}
              >
                {appointmentTypeDisplayNames[taxReturn.appointmentType as AppointmentType] || taxReturn.appointmentType}
              </Badge>
              {taxReturn.appointmentType === 'in-person' && (
                <span style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)' }}>{t('overviewTab.appointments.inPersonNote')}</span>
              )}
              {requiresRemoteSignature(taxReturn.appointmentType) && (
                <span style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)' }}>{t('overviewTab.appointments.remoteSignatureNote')}</span>
              )}
            </div>
            <div className="detail-row">
              <span className="detail-label">{t('overviewTab.appointments.scheduledLabel')}</span>
              <span className="detail-value">
                {taxReturn.scheduledDate
                  ? format(new Date(taxReturn.scheduledDate), 'MMMM d, yyyy h:mm a')
                  : '-'}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">{t('overviewTab.appointments.servicesLabel')}</span>
              <span className="detail-value">{taxReturn.services || '-'}</span>
            </div>
          </div>

          {/* Linked Appointments */}
          {additionalAppointmentIds.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
              <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--tl-text-secondary)' }}>{t('overviewTab.appointments.additionalHeading')}</div>
              {additionalAppointmentIds.map((apptId, idx) => (
                <div
                  key={apptId}
                  style={{
                    padding: '12px',
                    backgroundColor: 'var(--tl-bg-page)',
                    borderRadius: '8px',
                    border: '1px solid var(--tl-border)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Badge size="small" appearance="outline">
                      {t('overviewTab.appointments.linkedBadge', { number: idx + 1 })}
                    </Badge>
                    <span style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>
                      {t('overviewTab.appointments.appointmentIdLabel', { id: apptId.substring(0, 8) })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Other Returns for this Appointment */}
          {appointmentReturns && appointmentReturns.length > 0 && (
            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--tl-border)' }}>
              <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--tl-text-secondary)', marginBottom: '8px' }}>
                {t('overviewTab.appointments.otherReturnsHeading')}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {appointmentReturns.map(ret => (
                  <Link
                    key={ret.id}
                    to={`/tax-returns/${ret.id}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px',
                      backgroundColor: 'var(--tl-bg-page)',
                      borderRadius: '6px',
                      border: '1px solid var(--tl-border)',
                      textDecoration: 'none',
                      color: 'inherit',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {ret.returnType === 'business' ? (
                        <BuildingRegular style={{ color: 'var(--tl-success)' }} />
                      ) : (
                        <PersonRegular style={{ color: 'var(--tl-brand)' }} />
                      )}
                      <span style={{ fontWeight: 500 }}>{ret.clientName}</span>
                      <Badge size="small" appearance="outline">{ret.taxYear}</Badge>
                      <Badge size="small" appearance="outline">
                        {ret.returnType === 'business' ? t('overviewTab.appointments.businessBadge') : ret.filingType}
                      </Badge>
                    </div>
                    <Badge
                      size="small"
                      style={{
                        backgroundColor: statusColors[ret.status],
                        color: 'white',
                      }}
                    >
                      {statusDisplayNames[ret.status]}
                    </Badge>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Add Return Button */}
          <div style={{ marginTop: '16px' }}>
            <Button
              appearance="secondary"
              icon={<AddRegular />}
              size="small"
              onClick={onAddReturnDialogOpen}
            >
              {t('overviewTab.appointments.addReturn')}
            </Button>
          </div>
        </div>

        {/* AI Extracted Data */}
        {taxReturn.extractedData && (
          <div className="detail-section">
            <h3>{t('overviewTab.summary.heading')}</h3>
            <div className="detail-row">
              <span className="detail-label">{t('overviewTab.summary.resultLabel')}</span>
              <span className="detail-value" style={{ textTransform: 'capitalize' }}>
                {taxReturn.extractedData.refundOrPayment === 'refund' && (
                  <span style={{ color: 'var(--tl-success)' }}>{t('overviewTab.summary.refund')}</span>
                )}
                {taxReturn.extractedData.refundOrPayment === 'payment' && (
                  <span style={{ color: 'var(--tl-danger)' }}>{t('overviewTab.summary.paymentDue')}</span>
                )}
                {taxReturn.extractedData.refundOrPayment === 'zero' && (
                  <span>{t('overviewTab.summary.zeroBalance')}</span>
                )}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">{t('overviewTab.summary.amountLabel')}</span>
              <span className="detail-value">
                ${taxReturn.extractedData.amount.toFixed(2)}
              </span>
            </div>
            {taxReturn.extractedData.agi && (
              <div className="detail-row">
                <span className="detail-label">{t('overviewTab.summary.agiLabel')}</span>
                <span className="detail-value">
                  ${taxReturn.extractedData.agi.toFixed(2)}
                </span>
              </div>
            )}
            {taxReturn.extractedData.filingStatus && (
              <div className="detail-row">
                <span className="detail-label">{t('overviewTab.summary.filingStatusLabel')}</span>
                <span className="detail-value">{taxReturn.extractedData.filingStatus}</span>
              </div>
            )}
          </div>
        )}
      </div>

    </>
  );
}
